import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useOrganization } from "@/hooks/use-organization";
import { unwrapFunctionInvoke, getFriendlyMessage, logError } from "@/lib/error-utils";

interface GymMasterMember {
  id: string | number;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phone?: string;
  membershipStatus?: string;
}

interface SyncResult {
  success: boolean;
  message?: string;
  error?: string;
  cancelled?: number;
  member?: GymMasterMember;
  members?: GymMasterMember[];
  member_id?: string;
}

export function useGymMasterSync() {
  const { toast } = useToast();
  const { organization } = useOrganization();
  const [syncing, setSyncing] = useState(false);
  const [fetchingMembers, setFetchingMembers] = useState(false);

  const gymmasterConfig = organization?.settings?.integrations?.gymmaster;
  const isEnabled = gymmasterConfig?.enabled ?? false;

  /**
   * Link a TrainerPulse client to a GymMaster member
   */
  const linkMember = useCallback(async (clientId: string, gymmasterMemberId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("clients")
        .update({ gymmaster_member_id: gymmasterMemberId })
        .eq("id", clientId);

      if (error) throw error;

      toast({
        title: "Member linked",
        description: "Client successfully linked to GymMaster member",
      });
      return true;
    } catch (error: any) {
      toast({
        title: "Error linking member",
        description: error.message || "Failed to link member",
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);

  /**
   * Unlink a TrainerPulse client from GymMaster
   */
  const unlinkMember = useCallback(async (clientId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("clients")
        .update({ gymmaster_member_id: null })
        .eq("id", clientId);

      if (error) throw error;

      toast({
        title: "Member unlinked",
        description: "Client unlinked from GymMaster member",
      });
      return true;
    } catch (error: any) {
      toast({
        title: "Error unlinking member",
        description: error.message || "Failed to unlink member",
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);

  /**
   * Cancel membership in GymMaster when client goes inactive
   */
  const syncCancellation = useCallback(async (clientId: string, reason?: string): Promise<SyncResult> => {
    if (!isEnabled) {
      return { success: false, error: "GymMaster integration is not enabled" };
    }

    setSyncing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const result = await supabase.functions.invoke("gymmaster-sync", {
        body: {
          action: "cancel_membership",
          client_id: clientId,
          reason,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const data = unwrapFunctionInvoke<SyncResult>(result);

      if (data?.success) {
        toast({
          title: "Membership cancelled",
          description: data.message || "Successfully cancelled GymMaster membership",
          duration: 6000,
        });
        return { success: true, message: data.message, cancelled: data.cancelled };
      } else {
        throw new Error(data?.error || "Failed to cancel membership");
      }
    } catch (error: any) {
      const f = getFriendlyMessage(error);
      logError(error, "gymmaster-cancel-membership");
      toast({
        title: f.title,
        description: `${f.userMessage} (code: ${f.code})`,
        variant: "destructive",
      });
      return { success: false, error: error.message };
    } finally {
      setSyncing(false);
    }
  }, [isEnabled, toast]);

  /**
   * Fetch all members from GymMaster for linking
   */
  const fetchMembers = useCallback(async (): Promise<GymMasterMember[]> => {
    if (!isEnabled) {
      toast({
        title: "Integration disabled",
        description: "GymMaster integration is not enabled",
        variant: "destructive",
      });
      return [];
    }

    setFetchingMembers(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const result = await supabase.functions.invoke("gymmaster-sync", {
        body: { action: "get_members" },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const data = unwrapFunctionInvoke<SyncResult>(result);

      if (data?.success) {
        return data.members || [];
      } else {
        throw new Error(data?.error || "Failed to fetch members");
      }
    } catch (error: any) {
      const f = getFriendlyMessage(error);
      logError(error, "gymmaster-get-members");
      toast({
        title: f.title,
        description: `${f.userMessage} (code: ${f.code})`,
        variant: "destructive",
      });
      return [];
    } finally {
      setFetchingMembers(false);
    }
  }, [isEnabled, toast]);

  /**
   * Get a specific member from GymMaster
   */
  const getMember = useCallback(async (memberId: string): Promise<GymMasterMember | null> => {
    if (!isEnabled) {
      return null;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const result = await supabase.functions.invoke("gymmaster-sync", {
        body: { action: "get_member", member_id: memberId },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const data = unwrapFunctionInvoke<SyncResult>(result);

      if (data?.success) {
        return data.member || null;
      }
      return null;
    } catch (error: any) {
      // Silent lookup — capture but don't toast (caller treats null as "no link").
      logError(error, "gymmaster-get-member");
      return null;
    }
  }, [isEnabled]);

  /**
   * Create a new member in GymMaster from a TrainerPulse client
   */
  const createMember = useCallback(async (clientId: string): Promise<SyncResult> => {
    if (!isEnabled) {
      return { success: false, error: "GymMaster integration is not enabled" };
    }

    setSyncing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const result = await supabase.functions.invoke("gymmaster-sync", {
        body: {
          action: "create_member",
          client_id: clientId,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const data = unwrapFunctionInvoke<SyncResult>(result);

      if (data?.success) {
        toast({
          title: "Member created",
          description: data.message || "Successfully created member in GymMaster",
        });
        return { success: true, message: data.message, member_id: data.member_id };
      } else {
        throw new Error(data?.error || "Failed to create member");
      }
    } catch (error: any) {
      const f = getFriendlyMessage(error);
      logError(error, "gymmaster-create-member");
      toast({
        title: f.title,
        description: `${f.userMessage} (code: ${f.code})`,
        variant: "destructive",
      });
      return { success: false, error: error.message };
    } finally {
      setSyncing(false);
    }
  }, [isEnabled, toast]);

  return {
    isEnabled,
    syncing,
    fetchingMembers,
    linkMember,
    unlinkMember,
    syncCancellation,
    createMember,
    fetchMembers,
    getMember,
  };
}
